import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { setAttributionCtxProvider, getAttributionCtxGlobal } from "@/hooks/useAttribution";

// Re-export so App.tsx can register the provider once
export { setAttributionCtxProvider };

let authExpiryDispatched = false;

function dispatchAuthExpired() {
  if (typeof window === "undefined") return;
  if (!window.localStorage.getItem("auth_token")) return;
  if (authExpiryDispatched) return;
  authExpiryDispatched = true;
  window.dispatchEvent(new CustomEvent("auth:expired"));
  // Reset guard after redirect occurs (3 s safety valve)
  setTimeout(() => { authExpiryDispatched = false; }, 3000);
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 401) {
      dispatchAuthExpired();
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function getPartnerHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const userId = localStorage.getItem('partnerUserId');
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  
  if (userId) {
    headers['x-partner-user-id'] = userId;
  }
  if (restaurantId) {
    headers['x-restaurant-id'] = restaurantId;
  }
  if (eatzyToken) {
    headers['x-eatzy-token'] = eatzyToken;
  }
  return headers;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window === "undefined") {
    return headers;
  }
  const token = window.localStorage.getItem("auth_token");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getAttributionHeaders(method: string): Record<string, string> {
  const writeMethod = ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase());
  if (!writeMethod) return {};
  try {
    const ctx = getAttributionCtxGlobal();
    if (ctx) {
      return { 'x-attribution-context': JSON.stringify(ctx) };
    }
  } catch {
    // ignore
  }
  return {};
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isPartnerRoute = url.includes('/api/partner/');
  const partnerHeaders = isPartnerRoute ? getPartnerHeaders() : {};
  const authHeaders = getAuthHeaders();
  const attributionHeaders = getAttributionHeaders(method);
  
  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...partnerHeaders,
      ...authHeaders,
      ...attributionHeaders,
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const isPartnerRoute = url.includes('/api/partner/');
    const partnerHeaders = isPartnerRoute ? getPartnerHeaders() : {};
    const authHeaders = getAuthHeaders();
    
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        ...partnerHeaders,
        ...authHeaders,
      },
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
